import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftCircle,
  Calendar,
  Download,
  FileText,
  Loader2,
  Award,
  User,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAssignment, useAssignmentSubmission, useGradeAssignment } from '@/hooks/useAssignments'
import { useCourse } from '@/hooks/useCourses'
import { useUser } from '@/hooks/useUsers'
import { useAuth } from '@/contexts/AuthContext'
import { assignmentService } from '@/services/assignmentService'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

const statusStyles: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-700',
  graded: 'bg-green-100 text-green-700',
  returned: 'bg-yellow-100 text-yellow-700',
  late: 'bg-red-100 text-red-700',
  draft: 'bg-gray-100 text-gray-600',
}

export function SubmissionDetailPage() {
  const { assignmentId, submissionId } = useParams<{ assignmentId: string; submissionId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const {
    data: assignment,
    isLoading: assignmentLoading,
    error: assignmentError,
  } = useAssignment(assignmentId ?? '')

  const {
    data: submission,
    isLoading: submissionLoading,
    error: submissionError,
  } = useAssignmentSubmission(assignmentId, submissionId)

  const { data: learner } = useUser(submission?.learnerId)
  const { data: course } = useCourse(assignment?.courseId)

  const [grade, setGrade] = useState<number | ''>('')
  const [feedback, setFeedback] = useState('')

  const gradeSubmission = useGradeAssignment()

  const isInstructor = user?.role === 'admin' || user?.role === 'instructor'

  useEffect(() => {
    if (submission) {
      const gradePoints = submission.grade?.points
      setGrade(gradePoints !== undefined && gradePoints !== null ? gradePoints : '')
      setFeedback(submission.grade?.feedback ?? '')
    }
  }, [submission])

  const learnerName = useMemo(() => {
    if (!learner) return 'Learner'
    return `${learner.firstName ?? ''} ${learner.lastName ?? ''}`.trim() || learner.email || 'Learner'
  }, [learner])

  const learnerEmail = learner?.email

  if (!assignmentId || !submissionId) {
    return null
  }

  if (assignmentLoading || submissionLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (assignmentError || submissionError || !assignment || !submission) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <FileText className="h-5 w-5" />
              Submission unavailable
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not load this submission. Return to the submissions list and try again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const submittedAt = submission.submittedAt
    ? new Date(submission.submittedAt).toLocaleString()
    : 'Not submitted'
  const gradePoints = submission.grade?.points
  const gradeMax = assignment.maxPoints ?? submission.grade?.maxPoints;
  const gradeLabel =
    gradePoints !== undefined && gradePoints !== null
      ? `${gradePoints}/${gradeMax ?? assignment.maxPoints}`
      : 'Not graded'
  const courseTitle = course?.title ?? assignment.courseId

  const handleBack = () =>
    navigate(paths.app.assignments.submissions.list.build(assignment.id))

  const handleDownload = async (fileUrl: string, fileName: string | null | undefined) => {
    try {
      await assignmentService.downloadFile(fileUrl, fileName ?? 'submission-file')
    } catch (error) {
      console.error('Failed to download file:', error)
    }
  }

  const handleGradeSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (grade === '') return
    gradeSubmission.mutate(
      {
        assignmentId: assignment.id,
        submissionId,
        points: Number(grade),
        feedback,
      },
      {
        onSuccess: () => {
          navigate(paths.app.assignments.submissions.list.build(assignment.id))
        },
      }
    )
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div>
            <div className="flex items-center gap-2">
              <BackButton onClick={handleBack} />
              <p className="text-sm font-medium text-red-600">
                Submission for {assignment.title}
              </p>
            </div>
            <h3 className="flex items-center gap-2 mt-1 text-2xl font-bold text-gray-900">
              <User/> {learnerName}
            </h3>
            {learnerEmail && <p className="text-sm text-gray-500">{learnerEmail}</p>}
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 text-sm text-gray-600 sm:items-end">
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Submitted {submittedAt}
          </span>
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {courseTitle}
          </span>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Submission overview</CardTitle>
            <CardDescription>Review submission metadata and attachments.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 text-sm text-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={statusStyles[submission.status] ?? 'bg-gray-100 text-gray-600'}>
                {submission.status}
              </Badge>
              {submission.late && <Badge className="bg-red-100 text-red-700">Late</Badge>}
            </div>
            <div>
              <p className="font-medium text-gray-900">Assignment</p>
              <p className="mt-1 text-gray-600">{assignment.title}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="font-medium text-gray-900">Submitted</p>
                <p className="mt-1 text-gray-600">{submittedAt}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Current grade</p>
                <p className="mt-1 text-gray-600">{gradeLabel}</p>
              </div>
            </div>
            <div>
              <p className="font-medium text-gray-900">Attachments</p>
              {submission.files.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">No files uploaded for this submission.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {submission.files.map((file) => (
                    <div
                      key={file.id}
                      className="flex flex-col gap-3 rounded-md border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {file.fileName ?? 'Submission file'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Uploaded {new Date(file.uploadedAt).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(file.url, file.fileName)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {submission.grade?.feedback && (
              <div>
                <p className="font-medium text-gray-900">Instructor feedback</p>
                <p className="mt-2 whitespace-pre-line text-gray-600">{submission.grade.feedback}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Assignment summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-gray-700">
            <div>
              <p className="font-medium text-gray-900">Course</p>
              <p className="mt-1 text-gray-600">{courseTitle}</p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Due date</p>
              <p className="mt-1 text-gray-600">
                {new Date(assignment.dueDate).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-gray-500" />
              <span>Max points: {assignment.maxPoints}</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {isInstructor && (
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Grade submission</CardTitle>
              <CardDescription>Record a score and share feedback with the learner.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleGradeSubmit}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Score (out of {assignment.maxPoints})
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={assignment.maxPoints}
                    value={grade === '' ? '' : grade}
                    onChange={(event) => {
                      const value = event.target.value
                      setGrade(value === '' ? '' : Number(value))
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Feedback</label>
                  <Textarea
                    rows={4}
                    value={feedback}
                    onChange={(event) => setFeedback(event.target.value)}
                    placeholder="Share detailed comments, rubric notes, or next steps."
                  />
                </div>
                <Button type="submit" disabled={gradeSubmission.isPending} className="flex items-center gap-2">
                  {gradeSubmission.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {gradeSubmission.isPending ? 'Saving grade...' : 'Save grade'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  )
}

export default SubmissionDetailPage
